import { Fragment, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as formActions from "../../../store/actions/forms";
import * as appActions from "../../../store/actions/app";
import {
  alphabeticTest,
  dateTimeTest,
  emailLengthProps,
  emailTest,
  freeFormTest,
  length100Props,
  length1333Props,
  length40Props,
  length50Props,
  phoneProps,
  phoneTest,
  trueFalseRequired,
  univMinDate,
  yearProps,
  yearTest,
  zipProps,
  zipTest
} from "../../../utilities/validationUtilities"
import Pill from "../../ui/pill/Pill";
import styles from "./AutoForm.module.css";
import {
  states,
  countries,
  expandedStates,
  Canada,
  US,
  Mexico,
  unknownName,
} from "../../../constants/data";
import {
  validationError,
  baseTitle,
  mandatoryFieldsMessage,
} from "../../../constants/messages";
import UploadFile from "../../upload-file/UploadFile";
import FileIcon from "../../upload-file/file-icon/FileIcon";
import { useHistory } from "react-router-dom";
import useDocuments from "../../../hooks/useDocuments";
import {
  documentDelivery,
  vehicleLocationType,
  vehicleMakes,
  vehicleOwnerOptions,
  yesNoUnsure,
} from "../../../constants/options";
import { dateInRangeIncludesStart, dateToMoment } from "../../../utilities/dateUtilities";
import { PERSONAL, COMMERCIAL } from "../../../constants/keys";
import {
  countryToCode,
  textToBoolean,
} from "../../../utilities/dataUtilities";
import inputUtilities from "../../../utilities/forms/formInputUtilities";
import { createValidator, validateFieldset, validatePersonArray } from "../../../utilities/forms/formValidationUtilities";
import { fieldsToStrings, mapPersonArray, mapPassengerArray } from "../../../utilities/forms/formSubmissionUtilities";
import { yesNo } from "../../../constants/options";

function AutoForm() {
  const history = useHistory();
  const dispatch = useDispatch();
  let autoPolicies = useSelector((state) => state.forms.autoPolicies);
  let vehiclePolicies = useSelector((state) => state.forms.vehiclePolicies);
  const [policies, setPolicies] = useState([]);
  let activePolicy = useSelector((state) => state.policies.activePolicy);
  let policyType = useSelector((state) => state.app.policyType);
  let policyData = useSelector((state) => state.forms.policyRetrievalData);
  let insuredUser = useSelector((state) => state.user.userInfo);
  let policyVehicles = useSelector(
    (state) => state.forms.policyRetrievalData?.vehicleList
  );
  let drivers = useSelector((state) => state.forms.drivers);
  let lossCauses = useSelector((state) => state.forms.lossCauses).filter(
    (item) => item.Auto
  );
  let config = useSelector((state) => state.app.config);
  let [policySelectOptions, setPolicySelectOptions] = useState(null);
 const insuredOptions = [
  { text: "A listed insured", value: true },
  { text: "Another driver", value: false }
];

  const now = new Date();
  now.setSeconds(59);

  const selectStatePlaceholder = "Select a state";
  const rows3Props = { rows: "3" };
  const upToNowProps = { minDate: dateToMoment(univMinDate), maxDate: dateToMoment(now) };
  const maxPassengers = 4;

  const policy = {
    policy: {
    label: "Policy",
    value: "",
    displayValue: "",
    required: true,
    }
  };

  const [policyField, setPolicyField] = useState(policy);

  const policyUtilities = inputUtilities(policyField, setPolicyField, styles);
  const createPolicySelect = policyUtilities.createSearchableSelect;
  const handlePolicyChange = policyUtilities.handleFieldChange;

  const fields = {
    whereDate: {
      label: "Date / Time",
      value: null,
      required: true,
      additionalProps: upToNowProps,
      regex: dateTimeTest,
    },
    contactFirstName: {
      label: "Contact First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    contactLastName: {
      label: "Contact Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    contactEmail: {
      label: "Contact Email",
      value: "",
      regex: emailTest,
      additionalProps: emailLengthProps,
    },
    contactPhone: {
      label: "Contact Phone Number",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    contactDocumentDelivery: {
      label: "What is the preferred method of delivering claim-related documents to the contact?",
      value: "",
    },
    whereAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    whereCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whereState: {
      label: "State",
      value: "",
      required: true,
      displayValue: null,
    },
    whereCounty: {
      label: "County",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whereZipcode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    whereCountry: {
      label: "Country",
      value: US.name,
    },
    whoIsTheInsured: {
      label: "Who was driving?",
      value: insuredOptions[0].value,
      required: true,
      fieldName: "whoIsTheInsured",
      validation: trueFalseRequired,
    },
    driverId: {
      label: "Driver Name",
      value: "",
      displayValue: null,
      required: true,
    },
    driverFirstName: {
      label: "Driver First Name",
      value: "",
    },
    driverLastName: {
      label: "Driver Last Name",
      value: "",
      required: true,
    },
    driverIsInjured: {
      label: "Was the driver injured?",
      value: false,
    },
    whoAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    whoCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    whoState: {
      label: "State",
      value: "",
      displayValue: null,
      required: true,
    },
    whoCounty: {
      label: "County",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest
    },
    whoZipcode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
      required: true,
    },
    pedestrianInjured: {
      label: "Were any pedestrians injured?",
      value: yesNoUnsure[2].value,
    },
    witnessesPresent: {
      label: "Were there any witnesses?",
      value: yesNoUnsure[2].value,
    },
    whatHappen: {
      label: "Tell us what happened?",
      value: "",
      required: true,
      regex: freeFormTest,
      additionalProps: { ...rows3Props, ...length1333Props },
    },
    lossCause: {
      label: "Which of the following best describes the cause of loss?",
      value: "",
      required: true,
      displayValue: null
    },
    policeAgencyContacted: {
      label: "Was a police agency contacted?",
      value: yesNoUnsure[2].value,
    },
    reportAgencyName: {
      label: "Agency Name",
      value: "",
      regex: freeFormTest,
    },
    reportNumber: {
      label: "Report Number",
      value: "",
      regex: freeFormTest,
    },
    vehicleIs: {
      label: "The vehicle is?",
      value: vehicleOwnerOptions[0].value,
      required: true,
    },
    vehicleId: {
      label: "Vehicle",
      value: "",
      required: true,
      displayValue: null,
    },
    vehicleYear: {
      label: "Year",
      value: "",
      additionalProps: yearProps,
      regex: yearTest,
    },
    vehicleMake: {
      label: "Make",
      value: "",
      displayValue: null,
    },
    vehicleModel: {
      label: "Model",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    vehicleVin: {
      label: "VIN",
      value: "",
      regex: freeFormTest,
    },
    rentalCompanyName: {
      label: "Company Name",
      value: "",
      required: true,
      regex: freeFormTest,
    },
    ownedFirstName: {
      label: "Owner First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    ownedLastName: {
      label: "Owner Last Name",
      value: "",
      required: true,
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    nonPolicyVehicleAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    nonPolicyVehicleCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    nonPolicyVehicleState: {
      label: "State",
      value: "",
      displayValue: null,
    },
    nonPolicyVehicleZipCode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    nonPolicyVehiclePhone: {
      label: "Phone",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    vehicleDescribe: {
      label: "Describe the vehicle damage",
      value: "",
      required: true,
      additionalProps: { ...rows3Props, ...length1333Props },
      regex: freeFormTest,
    },
    vehicleEstimateDamage: {
      label: "Estimate of damage",
      value: "",
      regex: freeFormTest,
    },
    vehicleDriveable: {
      label: "Is the vehicle driveable?",
      value: true,
    },
    werePassengers: {
      label: "Were there any passengers in this vehicle?",
      value: yesNoUnsure[2].value,
    },
    currentLocation: {
      label: "Location Type",
      value: "",
    },
    currentLocationVehicleAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    currentLocationVehicleCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    currentLocationVehicleState: {
      label: "State",
      value: null,
      displayValue: null,
    },
    currentLocationVehicleZip: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    otherVehicleInvolved: {
      label: "Were any other vehicles involved in the accident?",
      value: false,
      required: true,
      fieldName: "otherVehicleInvolved",
      validation: trueFalseRequired,
    },
    otherVehicleYear: {
      label: "Year",
      value: "",
      additionalProps: yearProps,
      regex: yearTest,
    },
    otherVehicleMake: {
      label: "Make",
      value: "",
      displayValue: null,
    },
    otherVehicleModel: {
      label: "Model",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    otherVehiclePlateNumber: {
      label: "Plate Number",
      value: "",
      additionalProps: length40Props,
      regex: freeFormTest,
    },
    otherVehicleState: {
      label: "State",
      value: "",
      displayValue: null,
    },
    otherVehicleDescribe: {
      label: "Describe the vehicle damage",
      value: "",
      additionalProps: { ...rows3Props, ...length1333Props },
      required: true,
      regex: freeFormTest,
    },
    otherVehicleDriverFirstName: {
      label: "Driver First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherVehicleDriverLastName: {
      label: "Driver Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherDriverIsOwner: {
      label: "Is this the owner?",
      value: true,
    },
    otherVehicleDriverInjured: {
      label: "Is the driver injured?",
      value: false
    },
    otherVehicleDriverAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    otherVehicleDriverCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherVehicleDriverState: {
      label: "State",
      value: "",
      displayValue: null,
    },
    otherVehicleDriverZipcode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    otherVehicleDriverPhone: {
      label: "Phone",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest
    },
    otherVehicleOwnerFirstName: {
      label: "Owner First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherVehicleOwnerLastName: {
      label: "Owner Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
      required: true,
    },
    otherVehicleOwnerAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    otherVehicleOwnerCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    otherVehicleOwnerState: {
      label: "State",
      value: "",
      displayValue: null,
    },
    otherVehicleOwnerZipcode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    otherVehicleOwnerPhone: {
      label: "Phone",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    werePassengersOtherVehicle: {
      label: "Were there any passengers in this vehicle?",
      value: yesNoUnsure[2].value,
    },
    damageToProperty: {
      label: "Was there any other damage to property other than vehicles?",
      value: false,
      required: true,
      fieldName: "damageToProperty",
      validation: trueFalseRequired
    },
    whoOwnsProperty: {
      label: "Do you know the owner of the property?",
      value: false,
      required: true,
      fieldName: "whoOwnsProperty",
      validation: trueFalseRequired
    },
    propertyOwnerFirstName: {
      label: "Property Owner First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerLastName: {
      label: "Property Owner Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
      required: true,
    },
    propertyOwnerAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    propertyOwnerCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerState: {
      label: "State",
      value: "",
      displayValue: null,
    },
    propertyOwnerCounty: {
      label: "County",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyOwnerZipcode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    propertyOwnerCountry: {
      label: "Country",
      value: US.name,
    },
    propertyOwnerPhone: {
      label: "Phone",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    propertyOwnerDescribeProperty: {
      label: "Describe the property",
      value: "",
      additionalProps: { ...rows3Props, ...length1333Props },
      required: true,
      regex: freeFormTest,
    },
    propertyOwnerDamageProperty: {
      label: "Describe the damage to the property",
      value: "",
      additionalProps: { ...rows3Props, ...length1333Props },
      required: true,
      regex: freeFormTest,
    },
    propertyLocationAddress: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    propertyLocationCity: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    propertyLocationState: {
      label: "State",
      value: "",
      displayValue: null,
    },
    propertyLocationZip: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
  };

  const witness = {
    firstName: {
      label: "Witness First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    lastName: {
      label: "Witness Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    witnessPhone: {
      label: "Witness Phone Number",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    }
  };

  const passenger = {
    firstName: {
      label: "First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    lastName: {
      label: "Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
     address: {
      label: "Address",
      value: "",
      additionalProps: length100Props,
      regex: freeFormTest,
    },
    city: {
      label: "City",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    state: {
      label: "State",
      value: "",
      displayValue: null,
    },
    zipCode: {
      label: "Zip Code",
      value: "",
      additionalProps: zipProps,
      regex: zipTest,
    },
    phoneNumber: {
      label: "Phone Number",
      value: "",
      additionalProps: phoneProps,
      regex: phoneTest,
    },
    isInjured: {
      label: "Is this passenger injured?",
      value: false,
    },
  };

  const pedestrian = {
    firstName: {
      label: "Pedestrian First Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    },
    lastName: {
      label: "Pedestrian Last Name",
      value: "",
      additionalProps: length50Props,
      regex: alphabeticTest,
    }
  };

  

  let [formFields, setFormFields] = useState(fields);
  let [witnessInfo, setWitnessInfo] = useState([]);
  let [passengerInfo, setPassengerInfo] = useState([]);
  let [otherPassengerInfo, setOtherPassengerInfo] = useState([]);
  let [pedestrianInfo, setPedestrianInfo] = useState([]);
  const [documents, setDocuments] = useState({ files: [], size: 0 });
  const handleDocuments = useDocuments(documents, setDocuments);

  let [witnessObj, setWitnessObj] = useState(null);
  let [passengerObj, setPassengerObj] = useState(null);
  let [pedestrianObj, setPedestrianObj] = useState(null);

  const [validatorsAdded, setValidatorsAdded] = useState(false);

  const {
    createInput,
    createTextArea,
    createDateInput,
    createToggleInput,
    createSelect,
    createSearchableSelect,
  } = inputUtilities(formFields, setFormFields, styles);

  const validateForm = () => {
    if (!validatePersonArray(pedestrianInfo, setPedestrianInfo, true)) {
      return false;
    }
    if (!validatePersonArray(witnessInfo, setWitnessInfo, true)) {
      return false;
    }
    if (!validatePersonArray(passengerInfo, setPassengerInfo, true)) {
      return false;
    }
    if (!validatePersonArray(otherPassengerInfo, setOtherPassengerInfo, true)) {
      return false;
    }
    let pageFields = Object.values(formFields)
      .map((obj, index) => {
        return { ...obj, index: index };
            });
    if (formFields.whoIsTheInsured.value === insuredOptions[0].value) {
      pageFields = pageFields.filter(
        (field) => 
          field.fieldName !== formFields.whoState.fieldName &&
          field.fieldName !== formFields.whoZipcode.fieldName
        );
        if (drivers?.length > 0 && policyType?.active === PERSONAL) {
          pageFields = pageFields.filter((field) => field.fieldName !== formFields.driverLastName.fieldName);
        } else {
          pageFields = pageFields.filter((field) => field.fieldName !== formFields.driverId.fieldName);
        }
    } else {
      pageFields = pageFields.filter((field) => field.fieldName !== formFields.driverId.fieldName);
    }
    if (formFields.vehicleIs.value !== vehicleOwnerOptions[0].value || policyVehicles?.length < 1) {
      pageFields = pageFields.filter((field) => field.fieldName !== formFields.vehicleId.fieldName);
    }
    if (formFields.vehicleIs.value !== vehicleOwnerOptions[1].value) {
      pageFields = pageFields.filter((field) => field.fieldName !== formFields.rentalCompanyName.fieldName);
    }
    if (formFields.vehicleIs.value !== vehicleOwnerOptions[2].value) {
      pageFields = pageFields.filter((field) => field.fieldName !== formFields.ownedLastName.fieldName);
    }
    if (!formFields.otherVehicleInvolved.value) {
      pageFields = pageFields.filter(
        (field) => 
        field.fieldName !== formFields.otherVehicleDescribe.fieldName &&
        field.fieldName !== formFields.otherVehicleOwnerLastName.fieldName
      );
    }
    if (formFields.otherDriverIsOwner.value) {
      pageFields = pageFields.filter((field) => field.fieldName !== formFields.otherVehicleOwnerLastName.fieldName);
    }
    if (!formFields.damageToProperty.value || !formFields.whoOwnsProperty.value) {
      pageFields = pageFields.filter(
        (field) => 
        field.fieldName !== formFields.propertyOwnerLastName.fieldName &&
        field.fieldName !== formFields.propertyOwnerDescribeProperty.fieldName &&
        field.fieldName !== formFields.propertyOwnerDamageProperty.fieldName
     );
    }
    return validateFieldset(pageFields, formFields, setFormFields, true);
  }; //validates fields on the form

  const addValidatorsNames = (theFields, setFields) => {
    let newFields = { ...theFields };
    for (let key in newFields) {
      if (
        newFields[key].fieldName !== formFields.whoIsTheInsured.fieldName &&
        newFields[key].fieldName !== formFields.otherVehicleInvolved.fieldName &&
        newFields[key].fieldName !== formFields.damageToProperty.fieldName &&
        newFields[key].fieldName !== formFields.whoOwnsProperty.fieldName  
      ) {
        const theField = newFields[key];
        newFields[key] = {
          ...theField,
          fieldName: key,
          validation: createValidator({
            ...theField.additionalProps,
            ...theField.regex,
            label: theField.label,
            required: theField.required,
          }),
        };
      }
    }
    setFields(newFields);
  }; //adds validation functions to fields objects, as well as fieldNames

  const submitForm = () => {
    if (validateForm()) {
      let formData = { ...formFields };
      formData = fieldsToStrings(formData);
      formData.lossCauseFull = formData.lossCause;
      formData.lossCause = lossCauses.find((item) => item.FullText === formData.lossCause)
          ?.Code;
      formData.whereCountry = countryToCode(formData.whereCountry);
      formData.whereProvenanceCanada = formData.whereState;
      formData.whereStateMexico = formData.whereState;
      formData.whereZipcodeCanada = formData.whereZipcode;
      formData.propertyOwnerProvenanceCanada = formData.propertyOwnerState;
      formData.propertyOwnerStateMexico = formData.propertyOwnerState;
      formData.propertyOwnerZipcodeCanada = formData.propertyOwnerZipcode;
      formData.propertyOwnerCountry = countryToCode(formData.propertyOwnerCountry);
      if (formData.vehicleIs !== vehicleOwnerOptions[1].value) {
        formData.rentalCompanyName = null;
      }
      formData.rentalAddress = formData.nonPolicyVehicleAddress;
      formData.rentalCity = formData.nonPolicyVehicleCity;
      formData.rentalState = formData.nonPolicyVehicleState;
      formData.rentalZipcode = formData.nonPolicyVehicleZipCode;
      formData.rentalPhone = formData.nonPolicyVehiclePhone;
      if (formData.vehicleIs !== vehicleOwnerOptions[2].value) {
        formData.ownedFirstName = null;
        formData.ownedLastName = null;
      }
      formData.ownedAddress = formData.nonPolicyVehicleAddress;
      formData.ownedCity = formData.nonPolicyVehicleCity;
      formData.ownedState = formData.nonPolicyVehicleState;
      formData.ownedZipcode = formData.nonPolicyVehicleZipCode;
      formData.ownedPhone = formData.nonPolicyVehiclePhone;
      delete formData.nonPolicyVehicleAddress;
      delete formData.nonPolicyVehicleCity;
      delete formData.nonPolicyVehicleState;
      delete formData.nonPolicyVehicleZipCode;
      delete formData.nonPolicyVehiclePhone;
      formData.witnesses = mapPersonArray(witnessInfo);
      formData.pedestrians = mapPersonArray(pedestrianInfo);
      formData.passengers = mapPassengerArray(passengerInfo);// 8/21 per Tina Kulesa we should not filter out empty names if the passenger is injured
      formData.otherPassengers = mapPassengerArray(otherPassengerInfo);
      formData.policySuccess = policyData?.policyNumber !== undefined;
      formData.pedestrianInjured = textToBoolean(formData.pedestrianInjured);
      formData.werePassengers = textToBoolean(formData.werePassengers);
      formData.werePassengersOtherVehicle = textToBoolean(formData.werePassengersOtherVehicle);
      formData.witnessesPresent = textToBoolean(formData.witnessesPresent);
      formData.policeAgencyContacted = textToBoolean(formData.policeAgencyContacted);
      formData.otherVehicleOwner = formData.otherDriverIsOwner;
      delete formData.otherDriverIsOwner;
      if(!formData.otherVehicleDriverFirstName && !formData.otherVehicleDriverLastName) {
        formData.otherVehicleDriverLastName = unknownName;
      }
      formData.policyNumber = policyField.policy.value.split("/")[0];
      const policyLob =
        activePolicy?.PolicyType ||
        policies.find((item) => item.policyNumber === policyField.policy.value.split("/")[0])
          ?.policyType;
      formData.lobCode = policyType.active + policyLob;

      dispatch(
        formActions.sendAutoForm(
          formData,
          documents.files,
          "Automobile",
          policyType.active,
          history
        )
      );
      dispatch(formActions.resetClearForm());
    } else {
      dispatch(appActions.setAlertModal(true, "error", validationError));
      setTimeout(() => {
        dispatch(appActions.setAlertModal(false, ""));
      }, 5000);
    }
  };


  const makeToName = (code) =>
    vehicleMakes.find((make) => make.code === code)?.name || code;

  useEffect(() => {
    if (formFields && !validatorsAdded) {
      addValidatorsNames(policyField, setPolicyField);
      addValidatorsNames(formFields, setFormFields);
      addValidatorsNames(pedestrian, setPedestrianObj);
      addValidatorsNames(witness, setWitnessObj);
      addValidatorsNames(passenger, setPassengerObj);
      setValidatorsAdded(true);
    }
  }, [formFields, validatorsAdded]); //eslint-disable-line react-hooks/exhaustive-deps
  //adds validators to fields

  const populatePersonArray = (personObj, setPersonInfo) => {
    const persons = [];
    for (let i = 0; i < 4; i++) {
      const thePerson = {...personObj};
      for (let fieldName in thePerson) {
        const labelSegments = thePerson[fieldName].label.split(" ");
        thePerson[fieldName] = {
          ...thePerson[fieldName],
          fieldName: `${thePerson[fieldName].fieldName}${i}`,
          label: `${labelSegments[0]} ${i + 1} ${labelSegments.filter((item, index) => index !== 0).join(" ")}`
        }
      }
      persons.push(thePerson);
    }
    setPersonInfo(persons);
  };//helper to populate witnesses, pedestrians, passengers arrays

  const addAnother = (e, personArray, personObj, set) => {
    e?.preventDefault(); // don't submit form
      
    const newArray = [...personArray];
    const newPerson = {...personObj};
    for (let fieldName in newPerson) {
      newPerson[fieldName] = {
        ...newPerson[fieldName],
        fieldName: `${newPerson[fieldName].fieldName}${personArray.length}`,
      };
    }
    newArray.push(newPerson);
    set(newArray);
  };

  const createPerson = (person, personArray, setPersonArray, desc, index) => {
    const setPerson = (item) => {
      const oldInfo = [...personArray];
      oldInfo[index] = item;
      setPersonArray(oldInfo);
    };
    const createPersonUtilities = inputUtilities(
      person,
      setPerson,
      styles
    );
    const createPersonInput = createPersonUtilities.createInput;
    const createPersonSelect = createPersonUtilities.createSearchableSelect;
    const createPersonToggle = createPersonUtilities.createToggleInput;
    return (
      <li key={index} className={styles.passengerItem}>
        <div className="formRow">
          {createPersonInput(person.firstName, "50%")}
          {createPersonInput(person.lastName, "50%")}
        </div>
        <div className="formRow">
          {createPersonInput(person.address, "50%")}
          {createPersonInput(person.city, "50%")}
        </div>
        <div className="formRow">
          {createPersonSelect(person.state, "40%", states, false, selectStatePlaceholder)}
          {createPersonInput(person.zipCode, "30%")}
          {createPersonInput(person.phoneNumber, "30%", "tel")}
        </div>
        <div className="formRow">
          {createPersonToggle(person.isInjured, "60%", yesNo)}
          <button
            type="button"
            className={styles.removeButton}
            onClick={() => {
                        const info = [...personArray];
                        info.splice(index, 1);
                        setPersonArray(info);
                      }
                    }
          >Remove {desc}</button>
        </div>
      </li>
    );
  };

  useEffect(() => {
    if (witnessObj && validatorsAdded && witnessInfo?.length < 1) {
      populatePersonArray(witnessObj, setWitnessInfo);
    }
  }, [witnessObj, validatorsAdded])//populate witnesses array

  useEffect(() => {
    if (pedestrianObj && validatorsAdded && pedestrianInfo?.length < 1) {
      populatePersonArray(pedestrianObj, setPedestrianInfo);
    }
  }, [pedestrianObj, validatorsAdded])//populate pedestrian array

  useEffect(() => {
    if (formFields.werePassengers.value && passengerInfo?.length < 1) {
      addAnother(null, passengerInfo, passengerObj, setPassengerInfo);//is flashing
    } else if (!formFields.werePassengers.value) {
      setPassengerInfo([]);
    }
  }, [formFields.werePassengers.value])

  useEffect(() => {
    if (formFields.werePassengersOtherVehicle.value && otherPassengerInfo?.length < 1) {
      addAnother(null, otherPassengerInfo, passengerObj, setOtherPassengerInfo);//is flashing
    } else if (!formFields.werePassengersOtherVehicle.value) {
      setOtherPassengerInfo([]);
    }
  }, [formFields.werePassengersOtherVehicle.value])

  useEffect(() => {
    dispatch(formActions.clearPolicyAssets());
    setFormFields(fields);
    setPedestrianInfo([]);
    setWitnessInfo([]);
    setPassengerInfo([]);
    setOtherPassengerInfo([]);
    setValidatorsAdded(false);
    setDocuments({ files: [], size: 0 });
  }, [policyField.policy.value]);//clears data on change in policy number

  useEffect(() => {
    if (policyType.active === PERSONAL) {
      setPolicies(autoPolicies);
    } else if (policyType.active === COMMERCIAL) {
      setPolicies(vehiclePolicies);
    }
  }, [policyType.active, autoPolicies, vehiclePolicies])

  useEffect(() => {
    if (validatorsAdded) {
      // If agent selected policy display only one policy option, otherwise all the policies for an insured
      if (activePolicy) {
       const policiesList = [
        {
          policyNumber: activePolicy.PolicyNumber,
          policyEffectiveDate: activePolicy.PolicyEffectiveDate,
          policyType: activePolicy.PolicyType,
        },
      ];
      let options = policiesList.map((policy) => {
        return {
          value: `${policy.policyNumber}/${policy.policyEffectiveDate}`,
          label: `${policy.policyNumber}/${policy.policyType}`
        };
      });
      handlePolicyChange(options[0].value, policyField.policy, true, options[0]);
      setPolicySelectOptions(options);
      } else {
        let options = policies.map((policy) => {
          return {
            value: `${policy.policyNumber}/${policy.policyEffectiveDate}`,
            label: `${policy.policyNumber}/${policy.policyType}`
          };
        });
        setPolicySelectOptions(options);
      }
    }
  }, [activePolicy, policies, validatorsAdded]);

  useEffect(() => {
    if (!activePolicy) {
      dispatch(formActions.getStates());
    }
  }, [dispatch, activePolicy]);

  useEffect(() => {
    document.title = baseTitle + " - Report Auto Claim";
  }, []);

  useEffect(() => {
    if (policyField.policy.value && formFields.whereDate.value) {
      const policyNumber = policyField.policy.value.substring(0, 7);
      if (
        policyData?.policyNumber !== policyNumber ||
        !dateInRangeIncludesStart(
          formFields.whereDate.value,
          policyData.effectiveDate,
          policyData.expirationDate
        )
      ) {
        //only retrieve again if actual change in policy or term
        const theFields = {...formFields};
        theFields.vehicleYear.value = "";
        theFields.vehicleMake.value = "";
        theFields.vehicleModel.value = "";
        theFields.vehicleVin.value = "";
        theFields.vehicleId.value = "";
        theFields.vehicleId.displayValue = "";
        setFormFields(theFields);
        dispatch(
          formActions.policySearchRetrieve(
            formFields.whereDate.value.format("YYYY-MM-DD"),
            policyNumber
          )
        );
        dispatch(appActions.setLoading(true));
      }
    } else {
      dispatch(formActions.clearPolicyAssets());
    } //if policy or datetime set to blank, clear policy
  }, [dispatch, formFields.whereDate.value, policyField.policy.value]);

  useEffect(() => {
    const theDriver = drivers?.find(
      (driver) => driver.contactId === formFields.driverId.value
    );
    const theFields = {...formFields};
    theFields.driverFirstName.value = theDriver ? theDriver.firstName : "";
    theFields.driverLastName.value = theDriver ? theDriver.lastName : "";
    theFields.whoAddress.value = "";
    theFields.whoCity.value = "";
    theFields.whoState.value = "";
    theFields.whoState.displayValue = "";
    theFields.whoCounty.value = "";
    theFields.whoZipcode.value = "";
    setFormFields(theFields);
  }, [formFields.driverId.value])

  useEffect(() => {
    const theFields = {...formFields};
    if (formFields.vehicleIs.value !== vehicleOwnerOptions[0].value) {
      theFields.vehicleYear.value = "";
      theFields.vehicleMake.value = "";
      theFields.vehicleModel.value = "";
      theFields.vehicleVin.value = "";
      theFields.vehicleId.value = "";
      theFields.vehicleId.displayValue = "";
    }
    setFormFields(theFields);
  }, [formFields.vehicleIs.value]);

  useEffect(() => {
      const theVehicle = policyVehicles?.find(
        (vehicle) => vehicle.policySystemId_Ext === formFields.vehicleId.value
      );
      const theFields = {...formFields};
      theFields.vehicleYear.value = theVehicle ? theVehicle.modelYear : "";
      theFields.vehicleMake.value = theVehicle ? makeToName(theVehicle.manufacturer) : "";
      theFields.vehicleMake.displayValue = theVehicle ? {
        label: makeToName(theVehicle.manufacturer),
        value: makeToName(theVehicle.manufacturer),
      } : "";
      theFields.vehicleModel.value = theVehicle ? theVehicle.model : "";
      theFields.vehicleVin.value = theVehicle ? theVehicle.vehicleIdentificationNumber : "";
      setFormFields(theFields);
  }, [formFields.vehicleId.value])

  useEffect(() => {
    const theFields = {...formFields};
    if (formFields.whoIsTheInsured.value === insuredOptions[0].value) {
      theFields.whoAddress.value = "";
      theFields.whoCity.value = "";
      theFields.whoState.value = "";
      theFields.whoState.displayValue = "";
      theFields.whoZipcode.value = "";
    } else if (formFields.whoIsTheInsured.value === insuredOptions[1].value) {
      theFields.driverId.value = "";
      theFields.driverId.displayValue = "";
    }
    theFields.driverFirstName.value = "";
    theFields.driverLastName.value = "";
    setFormFields(theFields);
  }, [formFields.whoIsTheInsured.value])

  useEffect(() => {
    const theFields = {...formFields};
    if (formFields.whereCountry.value === Canada.name) {
      theFields.whereState.label = "Province";
    } else {
      theFields.whereState.label = "State";
    }
    theFields.whereState.value = "";
    theFields.whereState.displayValue = "";
    setFormFields(theFields);
  },[formFields.whereCountry.value])//handles change in province/state label based on location country

  useEffect(() => {
    const theFields = {...formFields};
    if (formFields.propertyOwnerCountry.value === Canada.name) {
      theFields.propertyOwnerState.label = "Province";
    } else {
      theFields.propertyOwnerState.label = "State";
    }
    theFields.propertyOwnerState.value = "";
    theFields.propertyOwnerState.displayValue = "";
    setFormFields(theFields);
  },[formFields.propertyOwnerCountry.value])//handles change in province/state label based on property owner country

  const createPersonFactory = (personArray, setFunc, personObj) => {
    return personArray.map((person, index) => {
      const setPerson = (input) => {
        const oldPersonInfo = [...personArray];
        oldPersonInfo[index] = input;
        setFunc(oldPersonInfo);
      };
      const createPersonInput = inputUtilities(
        person,
        setPerson,
        styles
      ).createInput;
      const fieldNames = Object.keys(personObj)
      return (
        <div className={`formRow ${styles.personContainer}`} key={index}>
          {fieldNames.map((fieldName) => 
            createPersonInput(person[fieldName], `${100/fieldNames.length}%`)
          )}
        </div>
      );
    });
  };//display fields for each person in an array of person objects

  return (
    <div className={styles.container}>
      <div className="policyBar">
        {policySelectOptions &&
          createPolicySelect(
            policyField.policy,
            "33%",
            policySelectOptions,
            false,
            policySelectOptions?.length === 0 ? "" : "Select a Policy",
            true)}
        <h3 className={"titleText"}>Let's collect some details: </h3>
      </div>
      {policyField.policy.value && (
        <>
          <span className="redMessage">{mandatoryFieldsMessage}</span>

          <form className="mainForm">
            <div className="formColumn">
              <h3 className="secondaryTitleText">The Accident</h3>

              <Pill text="When" />

              <div className="formRow">
                {createDateInput(formFields.whereDate, "100%")}
              </div>

              <>
                <Pill text="Contact" />

                <div className="formRow">
                  {createInput(formFields.contactFirstName, "50%")}
                  {createInput(formFields.contactLastName, "50%")}
                </div>
                <div className="formRow">
                  {createInput(formFields.contactEmail, "50%")}
                  {createInput(formFields.contactPhone, "50%")}
                </div>
                {insuredUser && (
                  <div className="formRow">
                    {createSelect(formFields.contactDocumentDelivery, "100%", documentDelivery)}
                  </div>
                )}
              </>

              <Pill text="Where" />

              <div className="formRow">
                {createInput(formFields.whereAddress, "100%")}
              </div>
              <div className="formRow">
                {createInput(formFields.whereCity, "33%")}
                {formFields.whereCountry.value === US.name && (
                  <Fragment>
                    {createSearchableSelect(formFields.whereState, "33%", states, false, selectStatePlaceholder)}
                    {createInput(formFields.whereCounty, "33%")}
                  </Fragment>
                )}
                {formFields.whereCountry.value === Mexico.name && (
                  <Fragment>
                    {createInput(formFields.whereState, "67%")}
                  </Fragment>
                )}
                {formFields.whereCountry.value === Canada.name && (
                  <>
                   {createSearchableSelect(
                    formFields.whereState,
                    "67%",
                    expandedStates
                    .filter((state) => state.country.code === Canada.code)
                    .map((province) => {
                      return {
                        label: province.state,
                        value: province.Abbr
                      };
                    }),
                    false,
                    "Select a Province",
                    true
                    )}
                    </>
                )}
              </div>
              <div className="formRow">
                {createInput(formFields.whereZipcode, "50%")}
                {createSelect(formFields.whereCountry, "50%", countries.map(country => country.name))}
              </div>

              <Pill text="Who" />

              <div className="formRow">
                {createToggleInput(formFields.whoIsTheInsured, "60%", insuredOptions)}
              </div>
              {formFields.whoIsTheInsured.value && (
                <>
                <div className="formRow">
                  {policyType?.active === PERSONAL && drivers?.length > 0 ? (
                    <>
                    {createSearchableSelect(formFields.driverId,
                      "60%",
                      drivers?.map((driver) => {
                          return {
                            label: `${driver.firstName} ${driver.lastName}`,
                            value: driver.contactId,
                          };
                        }),
                        false,
                        "Select a driver",
                        true)}
                    </>
                  ) : (
                    <>
                      {createInput(formFields.driverFirstName, "50%")}
                      {createInput(formFields.driverLastName, "50%")}
                    </>
                  )}
                </div>
                <div className="formRow">
                  {createToggleInput(formFields.driverIsInjured, "60%", yesNo)}
                </div>
                </>
              )}

              {!formFields.whoIsTheInsured.value && (
                <div className={styles.hiddenDiv}>
                  <div className="formRow">
                    {createInput(formFields.driverFirstName, "50%")}
                    {createInput(formFields.driverLastName, "50%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.whoAddress, "100%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.whoCity, "33%")}
                    {createSearchableSelect(formFields.whoState, "33%", states, false, selectStatePlaceholder)}
                    {createInput(formFields.whoCounty, "33%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.whoZipcode, "33%")}
                  </div>
                    <div className="formRow">
                    {createToggleInput(formFields.driverIsInjured, "60%", yesNo)}
                  </div>
                </div>
              )}
              <div className="formRow">
                {createToggleInput(formFields.pedestrianInjured, "40%", yesNoUnsure)}
              </div>
              {textToBoolean(formFields.pedestrianInjured.value) && (
                <div className={styles.hiddenDiv}>
                  {pedestrianObj && createPersonFactory(pedestrianInfo, setPedestrianInfo, pedestrianObj)}
                </div>
              )}
              <div className="formRow">
                {createToggleInput(formFields.witnessesPresent, "40%", yesNoUnsure)}
              </div>
              {textToBoolean(formFields.witnessesPresent.value) && (
                <div className={styles.hiddenDiv}>
                  {createPersonFactory(
                    witnessInfo,
                    setWitnessInfo,
                    witnessObj
                    )}
                </div>
              )}

              <Pill text="What" />

              <div className="formRow">
                {createTextArea(formFields.whatHappen, "100%")}
              </div>
              <div className="formRow">
                {createSearchableSelect(formFields.lossCause, "100%", lossCauses.map(cause => cause.FullText), false)}
              </div>

              <Pill text="Report" />

              <div className="formRow">
              {createToggleInput(formFields.policeAgencyContacted, "40%", yesNoUnsure)}
              </div>
              {textToBoolean(formFields.policeAgencyContacted.value) && (
                <div className={styles.hiddenDiv}>
                  <div className="formRow">
                    {createInput(formFields.reportAgencyName, "50%")}
                    {createInput(formFields.reportNumber, "50%")}
                  </div>
                </div>
              )}
            </div>

            <div className="formColumn">
              <h3 className="secondaryTitleText">The Vehicles</h3>

              <Pill text="Your Vehicle" />

              <div className="formRow">
                {createToggleInput(formFields.vehicleIs, "100%", vehicleOwnerOptions)}
              </div>

              {formFields.vehicleIs.value === vehicleOwnerOptions[0].value && policyVehicles?.length > 0 && (
                <div className={styles.hiddenDiv}>
                  <div className="formRow">
                    {createSearchableSelect(
                      formFields.vehicleId,
                      "100%",
                      policyVehicles.map((vehicle) => {
                          return {
                            label: `${vehicle.modelYear || ""} ${
                              makeToName(vehicle.manufacturer) || ""
                            } ${vehicle.model || ""} ${
                              vehicle.vehicleIdentificationNumber || ""
                            }`,
                            value: vehicle.policySystemId_Ext,
                          };
                        }),
                        false,
                        "Select a vehicle on your policy",
                        true)}
                  </div>
                </div>
              )}

              {formFields.vehicleIs.value === vehicleOwnerOptions[0].value && !(policyVehicles?.length > 0) && (
                <div className="formRow">
                  {createInput(formFields.vehicleYear, "33%")}
                  {createSearchableSelect(formFields.vehicleMake, "33%", vehicleMakes.map(make => make.name), true, "Select a make")}
                  {createInput(formFields.vehicleModel, "33%")}
                </div>
              )}
              {(formFields.vehicleIs.value === vehicleOwnerOptions[1].value || formFields.vehicleIs.value === vehicleOwnerOptions[2].value) && (
                <div className="formRow">
                  {createInput(formFields.vehicleYear, "33%")}
                  {createSearchableSelect(formFields.vehicleMake, "33%", vehicleMakes.map(make => make.name), true, "Select a make")}
                  {createInput(formFields.vehicleModel, "33%")}
                </div>
              )}

              <div className="formRow">
                {createInput(formFields.vehicleVin, "100%")}
              </div>

              {(formFields.vehicleIs.value === vehicleOwnerOptions[1].value || formFields.vehicleIs.value === vehicleOwnerOptions[2].value) && (
                <div className={styles.hiddenDiv}>
                  <div className="formRow">
                    {formFields.vehicleIs.value === vehicleOwnerOptions[1].value ? (
                      <>
                      {createInput(formFields.rentalCompanyName, "100%")}
                      </>
                    ) : (
                      <>
                        {createInput(formFields.ownedFirstName, "50%")}
                        {createInput(formFields.ownedLastName, "50%")}
                      </>
                    )}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.nonPolicyVehicleAddress, "100%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.nonPolicyVehicleCity, "33%")}
                    {createSearchableSelect(formFields.nonPolicyVehicleState, "33%", states, true, selectStatePlaceholder)}
                    {createInput(formFields.nonPolicyVehicleZipCode, "33%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.nonPolicyVehiclePhone, "33%")}
                  </div>
                </div>
              )}

              <div className="formRow">
                {createTextArea(formFields.vehicleDescribe, "100%")}
              </div>
              <div className="formRow">
                {createInput(formFields.vehicleEstimateDamage, "100%")}
              </div>

              <div className="formRow">
                {createToggleInput(formFields.vehicleDriveable, "40%", yesNo)}
              </div>

              <div className="formRow">
              {createToggleInput(formFields.werePassengers, "40%", yesNoUnsure)}
              </div>
              {textToBoolean(formFields.werePassengers.value) && (
                <div className={styles.hiddenDiv}>
                  <ul>
                    {passengerInfo?.map((passenger, index) => createPerson(passenger, passengerInfo, setPassengerInfo, "Passenger", index))}
                  </ul>
                  {passengerInfo?.length < maxPassengers && (
                    <button
                      onClick={(event) => {
                        addAnother(event, passengerInfo, passengerObj, setPassengerInfo)
                      }}
                      className={styles.addPassenger}
                    >Add a Passenger</button>
                  )}
                </div>
              )}

              <Pill text="Current Vehicle Location" />
              <div className="formRow">
                {createSelect(formFields.currentLocation, "60%", vehicleLocationType)}
              </div>

              <div className="formRow">
                {createInput(formFields.currentLocationVehicleAddress, "100%")}
              </div>
              <div className="formRow">
                {createInput(formFields.currentLocationVehicleCity, "33%")}
                {createSearchableSelect(formFields.currentLocationVehicleState, "33%", expandedStates.map(state => state.Abbr), true, selectStatePlaceholder)}
                {createInput(formFields.currentLocationVehicleZip, "33%")}
              </div>

              <Pill text="Other Vehicle" />

              <div className="formRow">
                {createToggleInput(formFields.otherVehicleInvolved, "60%", yesNo)}
              </div>
              {formFields.otherVehicleInvolved.value && (
                <div className={styles.hiddenDiv}>
                  <div className="formRow">
                    {createInput(formFields.otherVehicleYear, "33%")}
                    {createSearchableSelect(formFields.otherVehicleMake, "33%", vehicleMakes.map(make => make.name), true, "Select a make")}
                    {createInput(formFields.otherVehicleModel, "33%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.otherVehiclePlateNumber, "50%")}
                    {createSearchableSelect(formFields.otherVehicleState, "50%", states, true, selectStatePlaceholder)}
                  </div>
                  <div className="formRow">
                    {createTextArea(formFields.otherVehicleDescribe, "100%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.otherVehicleDriverFirstName, "33%")}
                    {createInput(formFields.otherVehicleDriverLastName, "33%")}
                    {createToggleInput(formFields.otherDriverIsOwner, "33%", yesNo)}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.otherVehicleDriverAddress, "100%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.otherVehicleDriverCity, "33%")}
                    {createSearchableSelect(formFields.otherVehicleDriverState, "33%", states, true, selectStatePlaceholder)}
                    {createInput(formFields.otherVehicleDriverZipcode, "33%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.otherVehicleDriverPhone, "100%")}
                  </div>
                  <div className="formRow">
                    {createToggleInput(formFields.otherVehicleDriverInjured, "60%", yesNo)}              
                  </div>
                  {!formFields.otherDriverIsOwner.value && (
                    <div className={styles.hiddenDiv}>
                      <div className="formRow">
                        {createInput(formFields.otherVehicleOwnerFirstName, "50%")}
                        {createInput(formFields.otherVehicleOwnerLastName, "50%")}
                      </div>
                      <div className="formRow">
                        {createInput(formFields.otherVehicleOwnerAddress, "100%")}
                      </div>
                      <div className="formRow">
                        {createInput(formFields.otherVehicleOwnerCity, "33%")}
                        {createSearchableSelect(formFields.otherVehicleOwnerState, "33%", states, true, selectStatePlaceholder)}
                        {createInput(formFields.otherVehicleOwnerZipcode, "33%")}
                      </div>
                      <div className="formRow">
                        {createInput(formFields.otherVehicleOwnerPhone, "100%")}
                      </div>
                    </div>
                  )}

                  <div className="formRow">
                  {createToggleInput(formFields.werePassengersOtherVehicle, "40%", yesNoUnsure)}
                  </div>
                  {textToBoolean(formFields.werePassengersOtherVehicle.value) && (
                     <div className={styles.hiddenDiv}>
                      <ul>
                        {otherPassengerInfo?.map((passenger, index) => createPerson(passenger, otherPassengerInfo, setOtherPassengerInfo, "Passenger", index))}
                      </ul>
                      {
                        otherPassengerInfo?.length < maxPassengers && (
                        <button
                          onClick={(event) => {
                                  addAnother(event, otherPassengerInfo, passengerObj, setOtherPassengerInfo)
                                }}
                          className={styles.addPassenger}
                        >Add a Passenger</button>
                      )}
                      
                </div>
                  )}
                </div>
              )}

              <Pill text="Property" />

              <div className="formRow">
                {createToggleInput(formFields.damageToProperty, "75%", yesNo)}
              </div>
              {formFields.damageToProperty.value && (
                <div className="formRow">
                  {createToggleInput(formFields.whoOwnsProperty, "60%", yesNo)}
                </div>
              )}
              {formFields.whoOwnsProperty.value && formFields.damageToProperty.value && (
                <div className={styles.hiddenDiv}>
                  <div className="formRow">
                    {createInput(formFields.propertyOwnerFirstName, "50%")}
                    {createInput(formFields.propertyOwnerLastName, "50%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.propertyOwnerAddress, "100%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.propertyOwnerCity, "33%")}
                    {formFields.propertyOwnerCountry.value === US.name && (
                      <>
                      {createSearchableSelect(formFields.propertyOwnerState, "33%", states, true, selectStatePlaceholder)}
                      </>
                    )}
                    {formFields.propertyOwnerCountry.value === Mexico.name && (
                      <>
                      {createInput(formFields.propertyOwnerState, "33%")}
                      </>
                    )}
                    {formFields.propertyOwnerCountry.value === Canada.name && (
                      <>
                    
                       {
                       createSearchableSelect(
                        formFields.propertyOwnerState,
                        "33%",
                         expandedStates
                    .filter((state) => state.country.code === Canada.code)
                    .map((province) => {
                      return {
                        label: province.state,
                        value: province.Abbr
                      };
                    }),
                    false,
                    "Select a Province",
                    true
                    )}
                        </>
                    )}
                        
      
                    {createInput(formFields.propertyOwnerCounty, "33%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.propertyOwnerZipcode, "33%")}
                    {createSelect(formFields.propertyOwnerCountry, "33%", countries.map(country => country.name))}
                    {createInput(formFields.propertyOwnerPhone, "33%")}
                  </div>
                  <div className="formRow">
                    {createTextArea(formFields.propertyOwnerDescribeProperty, "100%")}
                  </div>
                  <div className="formRow">
                    {createTextArea(formFields.propertyOwnerDamageProperty, "100%")}
                  </div>
                  <div className="formRow">
                    <p className={styles.parLabel}>
                      Where can the damaged property be seen?
                    </p>
                  </div>
                  <div className="formRow">
                    {createInput(formFields.propertyLocationAddress, "100%")}
                  </div>
                  <div className="formRow">
                    {createInput(formFields.propertyLocationCity, "33%")}
                    {createSearchableSelect(formFields.propertyLocationState, "33%", expandedStates.map(state => state.Abbr), true, selectStatePlaceholder)}
                    {createInput(formFields.propertyLocationZip, "33%")}
                  </div>
                </div>
              )}

              <UploadFile
                getFiles={(files) => handleDocuments(files, "add")}
                size={(
                  documents.size / config.totalFileSizeLimitDivisor
                ).toFixed(2)}
              />

              <div className="documentContainer">
                {documents.files &&
                  documents.files.map((document, index) => {
                    return (
                      <FileIcon
                        key={index}
                        file={document}
                        removeFile={(file) => handleDocuments(file, "remove")}
                      />
                    );
                  })}
              </div>

              <button
                className={styles.reportButton}
                type="button"
                onClick={() => submitForm()}
              >
                Report Your Claim
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export default AutoForm;